/**
 * Sổ tiền theo chi nhánh (docs/plans/so-tien-theo-chi-nhanh.md, 3.4) — phần thuần của web quầy, không React.
 * Số tiền do MÁY CHỦ tính; ở đây chỉ quyết định có GHI chi nhánh lên dòng hay không và dòng phụ của thợ đọc ra sao.
 */
import type { PhanChiNhanhBaoCao } from './api.ts'
import { S } from './text/so-tien.ts'

/**
 * Hoá đơn / Bị huỷ có cần ghi tên chi nhánh trên từng dòng không? Có khi tiệm nhiều chi nhánh, hoặc danh sách đang xem có
 * hoá đơn của ≥ 2 chi nhánh khác nhau (chi nhánh đã ngừng vẫn còn hoá đơn). Tiệm một chi nhánh → không ghi (giao diện y hệt cũ).
 */
export function canNhanChiNhanh(
  ten: readonly (string | null | undefined)[],
  nhieu: boolean,
): boolean {
  if (nhieu) return true
  const khacNhau = new Set<string>()
  for (const t of ten) {
    if (t !== null && t !== undefined && t !== '') khacNhau.add(t)
  }
  return khacNhau.size >= 2
}

/**
 * Một dòng phụ của thợ: tên chi nhánh (bên trái) + tiền và "(gói …)" (bên phải, hai dòng nhỏ xếp dọc để không bẻ giữa số).
 * Cả hai '' khi thợ chỉ làm ở một chi nhánh; `tien` '' khi phần đó chỉ có buổi gói.
 */
/**
 * Nhãn "(đã ngừng)" ghi sau tên chi nhánh đã ngừng (soát giao diện N-b). Dấu cách TRONG nhãn là dấu cách không ngắt dòng
 * (U+00A0, viết bằng điểm mã để công cụ sửa tệp không làm mất) nên nhãn không bao giờ bị bẻ thành "(đã" / "ngừng)"; dấu cách ĐỨNG
 * TRƯỚC nhãn vẫn là dấu cách thường (được xuống dòng trước cả nhãn). Chữ gốc ở `text/so-tien.ts`.
 */
export const hauToDaNgung = ` ${S.bcDaNgung.trim().replaceAll(' ', String.fromCodePoint(0xa0))}`

export interface DongChiNhanhCuaTho {
  /** Khoá chi nhánh (dùng làm `key` của dòng; tên có thể trùng ở dữ liệu cũ). */
  khoa: string
  ten: string
  tien: string
  goi: string
}

/**
 * Các dòng phụ dưới tên thợ (Báo cáo › Thợ) — MỖI chi nhánh MỘT dòng riêng, tên bên trái, số bên phải (soát giao diện vòng 1:
 * nối " · " thành mảng chữ liền thì không đọc được). Thợ chỉ ở một chi nhánh → một dòng chỉ có tên. Nhiều chi nhánh: có tiền →
 * "400.000đ"; có buổi gói → thêm "(gói 598.000đ)"; chỉ có gói (tiền 0) → "(gói 598.000đ)" (không in "0đ" làm chủ tiệm tưởng thợ
 * không làm ở đó; tiền 0 và gói 0 đã bị máy chủ bỏ). Thứ tự = thứ tự máy chủ trả (cùng thứ tự khối "Cả tiệm"). Thiếu trường
 * (máy chủ cũ / tiệm một chi nhánh) → mảng rỗng.
 */
export function cacDongCuaTho(
  phan: readonly PhanChiNhanhBaoCao[] | undefined,
  dinhDangTien: (vnd: string) => string,
): DongChiNhanhCuaTho[] {
  if (phan === undefined || phan.length === 0) return []
  const [mot] = phan
  if (phan.length === 1 && mot !== undefined)
    return [{ khoa: mot.khoa, ten: mot.ten, tien: '', goi: '' }]
  return phan.map((p) => {
    const coTien = p.tienVnd !== '0'
    const coGoi = p.goiVnd !== '0'
    return {
      khoa: p.khoa,
      ten: p.ten,
      // Phần chỉ có buổi gói: không in "0đ"; tiền 0 và gói 0 đã bị máy chủ bỏ nên không có dòng trống.
      tien: coTien || !coGoi ? dinhDangTien(p.tienVnd) : '',
      goi: coGoi ? S.bcGoiNgan(dinhDangTien(p.goiVnd)) : '',
    }
  })
}

/**
 * MỘT đoạn gọn "Chính 4.000.000đ · Thủ Dầu Một 3.500.000đ" cho chip Thợ / Dịch vụ (phương án lùi của kế hoạch, rủi ro 2): danh sách
 * chi nhánh đầy đủ chỉ ở chip Ngày để danh sách thợ / dịch vụ không bị đẩy xuống. Chi nhánh đã ngừng ghi "(đã ngừng)".
 */
export function tomTatChiNhanh(
  ds: readonly { ten: string; daNgung: boolean; tienVnd: string }[],
  dinhDangTien: (vnd: string) => string,
): string {
  return ds
    .map((c) => S.bcTachCn(`${c.ten}${c.daNgung ? hauToDaNgung : ''}`, dinhDangTien(c.tienVnd)))
    .join(' · ')
}
